/**
 * Special training places (สถานที่ฝึกวิชาพิเศษ): a waterfall, a cave, a peak, a bamboo grove … inside
 * a zone's map, where cultivation builds faster while the player stays in them. Each is a circle in
 * tile coordinates. The map generator opens a clearing there and raises what the kind needs round
 * it (`shapeTrainingSpots` in map.ts: the cliff a waterfall falls from, the rock a cave is cut into,
 * the knoll of a peak); the World multiplies the cultivation rate inside (trainingSpots.ts); the
 * client draws the place's ambience and marks it on the maps. Add a place here and all of that
 * follows.
 *
 * The numbers (see docs/GDD.md): standing ×1.5 makes 1.5 a second (45 a payout), meditating ×1.5
 * makes 6 (180 a payout). Those the place suits get ×2: meditating that is 8 a second, the peak of
 * a perfect group dance, and never more (`TRAINING_SPOT.maxMult`).
 */
import type { Element } from './elements';
import { plains256, type ZoneId } from './zones';

export type TrainingSpotKind = 'waterfall' | 'cave' | 'peak' | 'bamboo' | 'hotSpring' | 'shrine' | 'shore';

/** Thai names of the kinds, for the panels and the map. */
export const TRAINING_SPOT_KINDS: Readonly<Record<TrainingSpotKind, string>> = {
  waterfall: 'น้ำตก',
  cave: 'ถ้ำ',
  peak: 'ยอดผา',
  bamboo: 'ป่าไผ่',
  hotSpring: 'น้ำพุร้อน',
  shrine: 'ศาลร้าง',
  shore: 'ริมทะเล',
};

/**
 * Whom a place favours: those whose active neigong is of an element, a school's disciples, those
 * fighting with an art, or anyone meditating there. Exactly one of the four is given.
 */
export interface SpotAffinity {
  readonly element?: Element;
  readonly school?: string;
  readonly art?: string;
  readonly meditation?: true;
  /** The multiplier for them, in place of the spot's own. */
  readonly mult: number;
}

export interface TrainingSpotDef {
  readonly id: string;
  readonly name: string;
  /** A line of flavour for the map and the toast. */
  readonly desc: string;
  readonly kind: TrainingSpotKind;
  readonly zone: ZoneId;
  /** Centre (tile coordinates) and radius in tiles: inside means closer to the centre than `r`. */
  readonly area: { readonly x: number; readonly y: number; readonly r: number };
  /** Cultivation × this while inside, standing or meditating. */
  readonly mult: number;
  readonly affinity?: SpotAffinity;
}

export const TRAINING_SPOT = {
  /** No place, however well it suits, multiplies cultivation by more than this. */
  maxMult: 2,
  /** How far behind the circle (tiles, toward the back of the view) a backdrop rises. */
  backdrop: 2.2,
} as const;

const list: TrainingSpotDef[] = [
  // ── หมู่บ้านหยานหยู: the first lessons, by the sea and under the western cliffs ──
  {
    id: 'wave-rocks',
    name: 'โขดหินฟังคลื่น',
    desc: 'โขดหินริมหาดที่ชาวประมงเล่าว่าเถาฉางเยี่ยเคยนั่งฟังคลื่น คลื่นซัดเข้า คลื่นถอยกลับ คลื่นแตกฝั่ง',
    kind: 'shore',
    zone: 'yanyu',
    // The wave cape's tip, down the ridge path from the pagoda rock.
    area: { x: 34.5, y: 17.5, r: 2.5 },
    mult: 1.5,
    affinity: { art: 'breeze-sword', mult: 2 },
  },
  {
    id: 'west-cliff-falls',
    name: 'น้ำตกผาตะวันตก',
    desc: 'สายน้ำจากเทือกเขาตะวันตกตกลงข้างลานฝึก เสียงน้ำกลบความคิดที่ฟุ้งซ่าน',
    kind: 'waterfall',
    zone: 'yanyu',
    // The falls pool in the training ground, under the western cliffs.
    area: { x: 25.5, y: 76.5, r: 3 },
    mult: 1.5,
  },
  // ── หมู่บ้านหยานหยู (ใหม่): the same first lessons, on the harbour beach ──
  {
    id: 'harbour-rocks',
    name: 'โขดหินปากอ่าว',
    desc: 'โขดหินริมหาดตะวันตกของท่าเรือ ที่ชาวประมงเล่าว่าเถาฉางเยี่ยเคยนั่งฟังคลื่น คลื่นซัดเข้า คลื่นถอยกลับ คลื่นแตกฝั่ง',
    kind: 'shore',
    zone: 'yanyu2',
    area: { x: 120.5, y: 556.5, r: 2.5 },
    mult: 1.5,
    affinity: { art: 'breeze-sword', mult: 2 },
  },
  // ── ลานศิลาเก้ากระบี่ ──
  {
    id: 'liu-shrine',
    name: 'ศาลร้างกลางทุ่ง',
    desc: 'ศาลเล็กที่ตระกูลหลิวสร้างไว้เซ่นไหว้เถาฉางเยี่ย ธูปดับไปนานแล้ว เหลือแต่ลมทุ่งที่สงบ',
    kind: 'shrine',
    zone: 'plains',
    // Inside the 72 m plains (zones.ts), east of the town, clear of its houses and the boss lair.
    area: { x: 56.5, y: 46.5, r: 3 },
    mult: 1.5,
  },
  // ── ภูเขายอดหมอก: softness that overcomes the hard ──
  {
    id: 'white-cloud-falls',
    name: 'น้ำตกเมฆขาว',
    desc: 'ละอองน้ำตกลอยขึ้นเป็นเมฆ นักพรตยอดหมอกมาฝึกใช้ความอ่อนสยบความแข็ง',
    kind: 'waterfall',
    zone: 'mist-peak',
    area: { x: 100.5, y: 96.5, r: 3 },
    mult: 1.5,
    affinity: { element: 'soft', mult: 2 },
  },
  {
    id: 'taiji-crag',
    name: 'ยอดผาหยินหยาง',
    desc: 'เนินหินที่นักพรตขึ้นไปรำมวยรับลม ฟ้ากว้าง ลมหายใจยาว หยินหยางหมุนเวียนไม่ขาดสาย',
    kind: 'peak',
    zone: 'mist-peak',
    area: { x: 138.5, y: 138.5, r: 3 },
    mult: 1.5,
    affinity: { element: 'taiji', mult: 2 },
  },
  // ── วัดหินระฆัง ──
  {
    id: 'damo-cave',
    name: 'ถ้ำตั๊กม้อ',
    desc: 'ถ้ำที่ปรมาจารย์ตั๊กม้อนั่งหันหน้าเข้าผนังเก้าปี เงียบจนได้ยินลมหายใจตัวเอง',
    kind: 'cave',
    zone: 'bell-stone',
    area: { x: 100.5, y: 98.5, r: 3 },
    mult: 1.5,
    affinity: { meditation: true, mult: 2 },
  },
  // ── ค่ายทางโคมเหล็ก ──
  {
    id: 'oath-shrine',
    name: 'ศาลสาบานองครักษ์',
    desc: 'ศาลร้างนอกค่ายที่องครักษ์รุ่นก่อนมาสาบานตน กฎที่ไร้ใจคือกระบี่ที่ไร้ด้าม',
    kind: 'shrine',
    zone: 'lantern-road',
    area: { x: 138.5, y: 100.5, r: 3 },
    mult: 1.5,
    affinity: { school: 'lantern-road', mult: 2 },
  },
  // ── หุบเขาจันทร์มัว ──
  {
    id: 'moon-spring',
    name: 'บ่อน้ำพุร้อนแสงจันทร์',
    desc: 'ไอร้อนลอยอ้อยอิ่งกลางหุบเขา กายผ่อนคลาย ลมปราณหยินไหลเวียนง่ายดาย',
    kind: 'hotSpring',
    zone: 'dim-moon',
    area: { x: 102.5, y: 138.5, r: 3 },
    mult: 1.5,
    affinity: { element: 'yin', mult: 2 },
  },
  // ── หอหมึกสน ──
  {
    id: 'brush-bamboo',
    name: 'ป่าไผ่ฟังพู่กัน',
    desc: 'ใบไผ่ไหวดั่งปลายพู่กัน หนึ่งขีดที่แม่น ดีกว่าร้อยขีดที่เร็ว',
    kind: 'bamboo',
    zone: 'pine-ink',
    area: { x: 100.5, y: 98.5, r: 3 },
    mult: 1.5,
    affinity: { art: 'ink-slate', mult: 2 },
  },
  // ── ผาดาบชาด ──
  {
    id: 'red-cliff',
    name: 'ยอดผาแดง',
    desc: 'ผาหินสีเลือดที่กระบี่คมโลหิตถูกดึงขึ้นมา หินยังอุ่น คมที่ไม่รู้จักหยุดย่อมกินเจ้าของ',
    kind: 'peak',
    zone: 'cinnabar',
    area: { x: 140.5, y: 100.5, r: 3 },
    mult: 1.5,
    affinity: { school: 'cinnabar', mult: 2 },
  },
  // ── ยอดเขาหิมะ: far and cold, a little more for anyone who comes ──
  {
    id: 'snow-pine-peak',
    name: 'ยอดสนหิมะ',
    desc: 'ยอดหินที่เถาฉางเยี่ยเคยใช้กิ่งสนแห้งแทนกระบี่ กิ่งที่ฟันตรงทางตัดหิมะได้เท่าเหล็ก',
    kind: 'peak',
    zone: 'snow',
    area: { x: 110.5, y: 108.5, r: 3.5 },
    mult: 1.75,
    affinity: { art: 'snow-sword', mult: 2 },
  },
];

export const TRAINING_SPOTS: readonly TrainingSpotDef[] = list;

/**
 * Where spots stand on the 256 m plains (PLAINS256, data/layouts/plains.ts): the field shrine in its clearing east of the
 * hub, inside its safe circle (R 5).
 */
const BIG_PLAINS: Readonly<Record<string, TrainingSpotDef['area']>> = {
  'liu-shrine': { x: 204.5, y: 152.5, r: 3 },
};
let bigPlains: readonly TrainingSpotDef[] | null = null;
/** The spots as placed now (the 256 m plains move theirs). */
const live = (): readonly TrainingSpotDef[] =>
  plains256() ? (bigPlains ??= list.map((s) => (s.zone === 'plains' && BIG_PLAINS[s.id] ? { ...s, area: BIG_PLAINS[s.id]! } : s))) : list;

/** The training spots of a zone. */
export const spotsIn = (zone: ZoneId): readonly TrainingSpotDef[] => live().filter((s) => s.zone === zone);

/** The spot a point of a zone's map lies in, or null. */
export function spotAt(zone: ZoneId, x: number, y: number): TrainingSpotDef | null {
  for (const s of live()) if (s.zone === zone && Math.hypot(x - s.area.x, y - s.area.y) < s.area.r) return s;
  return null;
}
